<template>
  <div class="pricing-list-item">
    <template v-if="productType">
      <div
        :class="[
          'pricing-list-item__wrapper',
          isPromotionalProduct && 'pricing-list-item__wrapper--promotional-subscription',
          isExpertEdgeEventItem && 'pricing-list-item__wrapper--expert-edge-event',
          isSkillDiveItem && 'pricing-list-item__wrapper--skill-dive',
        ]"
      >
        <button
          @click="removeItemShoppingCart(product)"
          class="btn btn--small btn--tertiary btn--training pricing-list-item__btn"
        />
        <p v-if="!isSkillDiveItem" class="pricing-list-item__selected-name">
          <span v-if="!isPromotionalProduct">
            {{ isFreeTrial ? `${productName} 7-Day Free Trial` : `${productName}` }}
          </span>
          <span v-else>
            {{ promoProductName }}
          </span>
        </p>
        <div
          v-if="isSkillDiveItem"
          class="pricing-list-item__selected-name pricing-list-item__selected-name--skill-dive"
        >
          <img
            :src="skillDiveLogoBadge"
            alt="Skill Dive Experience"
            class="pricing-list-item__skill-dive-icon"
          />
          <div class="pricing-list-item__skill-dive-add-ons">
            {{ skillDiveSelectedAddOns }}
          </div>
        </div>
        <p
          :class="[
            'pricing-list-item__selected-price',
            isFreeTrial && 'pricing-list-item__selected-price--free-trial',
          ]"
        >
          <span v-if="isFreeTrial">Free*</span>
          <span v-else>
            {{ `${currencySymbol}${productPrice}` }}
          </span>
        </p>
      </div>
      <div v-if="isPromotionalProduct" class="pricing-list-item__promo-warning">
        <template v-if="isEjptPromoSubscription">
          <Pill info class="pricing-list-item__selected-name-pill">
            <span class="pricing-list-item__selected-name-pill-text--hide-mobile">
              +3 months of Fundamentals Quarterly FREE*
            </span>
            <span class="pricing-list-item__selected-name-pill-text--hide-desktop">
              +3 mo Fundamentals Quarterly FREE*
            </span>
          </Pill>
          <p>*After 3 months, $117 billed quarterly.</p>
        </template>
        <template v-if="isEDAPromoSubscription">
          <Pill info class="pricing-list-item__selected-name-pill"> + 3 months of Premium* </Pill>
          <p>
            *After 3 months, you'll be billed $350 for 9 more months of Premium. Your subscription
            renews annually at $749.
          </p>
        </template>
        <template
          v-if="isExpertEdgeEventItem && !userHasSubscription && !isBusinessUser && !isBusinessUser"
        >
          <Pill
            info
            class="pricing-list-item__selected-name-pill pricing-list-item__selected-name-pill--expert-edge-event"
          >
            + 30 days of Premium*
          </Pill>
          <span> *Starts on day 1 of training ({{ product.trainingDayOneReadable }}) </span>
        </template>
        <p v-if="isFreeTrial">
          *Then {{ currencySymbol }}{{ productPrice }} per {{ product.term }}, billed
          {{ billingTypeString }}. After the trial your card will be charged (plus applicable taxes)
        </p>
        <p v-if="!isExpertEdgeEventItem">
          You can cancel your subscription at any time before the
          {{ `${isFreeTrial ? 'trial' : ''}` }}
          {{ `${isEjptPromoSubscription || isEDAPromoSubscription ? '3 months Free' : ''}` }}
          period ends in your profile settings.
        </p>
      </div>
      <div class="pricing-list-item__verbage" v-if="!isExpertEdgeEventItem">
        <div class="pricing-list-item__verbage-border"></div>
        <p class="pricing-list-item__verbage-text">
          Your INE Subscription will automatically renew at the end of each subscription term unless
          you cancel before your renewal date. You must cancel your subscription renewal before the
          scheduled renewal date to avoid the automatic renewal and any renewal fees associated with
          your subscription.
        </p>
      </div>
    </template>
    <div v-else class="pricing-list-item__wrapper">
      <button
        @click="removeItemShoppingCart(product)"
        class="btn btn--small btn--tertiary btn--training pricing-list-item__btn"
      />
      <p class="pricing-list-item__selected-name">{{ productName }}</p>
      <p class="pricing-list-item__selected-price">
        <span>{{ currencySymbol }}</span>
        <span>{{ productPrice }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { priceDetailUpdates, updateCart } from '@/helpers/mixins'
import CONSTANTS from '@/constants'
import Pill from '@/components/shared/Pill.vue'
import skillDiveLogo from '@/assets/images/hero-section/skill-dive/skill-dive-logo.png'

const {
  PLANS: { MONTHLY_EJPT, QUARTERLY_EJPT, EDA_PREMIUM, SKILL_DIVE_ANNUAL },
  EXPERT_EDGES_EVENTS: { CCIE_SECURITY_V6 },
} = CONSTANTS

export default {
  name: 'PricingListItem',
  mixins: [priceDetailUpdates, updateCart],
  components: {
    Pill,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      skillDiveLogoBadge: skillDiveLogo,
    }
  },
  computed: {
    ...mapState({
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
    }),
    productType() {
      const code = this.product?.merchant_id ?? ''
      const planCodes = Object.values(CONSTANTS.PLANS)
      const expertEdgesEventsCodes = Object.values(CONSTANTS.EXPERT_EDGES_EVENTS)
      // if true, is subscription
      return [...planCodes, ...expertEdgesEventsCodes].includes(code)
    },
    isFreeTrial() {
      return this.product?.plan_trial ?? ''
    },
    isPromotionalProduct() {
      const code = this.product?.merchant_id ?? ''
      return [MONTHLY_EJPT, QUARTERLY_EJPT, EDA_PREMIUM, CCIE_SECURITY_V6].includes(code)
    },
    isEjptPromoSubscription() {
      const code = this.product?.merchant_id ?? ''
      return [MONTHLY_EJPT, QUARTERLY_EJPT].includes(code)
    },
    isEDAPromoSubscription() {
      const code = this.product?.merchant_id ?? ''
      return EDA_PREMIUM === code
    },
    isExpertEdgeEventItem() {
      const code = this.product?.merchant_id ?? ''
      return [CCIE_SECURITY_V6].includes(code)
    },
    isSkillDiveItem() {
      return this.product.merchant_id === SKILL_DIVE_ANNUAL
    },
    skillDiveSelectedAddOns() {
      return this.isSkillDiveItem
        ? this.product.addOnsOnCart.map(SDAddOn => SDAddOn.label).join(', ')
        : false
    },
    promoProductName() {
      let name = ''
      switch (this.product?.merchant_id) {
        case MONTHLY_EJPT:
        case QUARTERLY_EJPT:
          name = 'Junior Penetration Tester Exam Voucher'
          break
        case EDA_PREMIUM:
          name = 'Enterprise Defense Associate Exam Voucher'
          break
        case CCIE_SECURITY_V6:
          name = this.product?.name
          break
      }
      return name
    },
    productName() {
      const name = this.product?.name ?? ''
      return name.replace('INE', '')
    },
    productPrice() {
      const price = this.product?.price_cents ?? 0
      return price / 100
    },
    billingTypeString() {
      return this.product.term === 'month' ? 'monthly' : 'annually'
    },
    selectedPlanTerm() {
      const selectedTerm = this.product.term
      if (['year', 'month', 'quarterly'].includes(selectedTerm)) return selectedTerm
      return '2yr'
    },
    userHasSubscription() {
      return this.usersCurrentSubscriptions.length > 0
    },
  },
  methods: {
    removeItemShoppingCart(item) {
      if (item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
        item.addOnsOnCart = []
        item.selectedAsAddOns = false
      }
      this.removeSelectedFromShoppingCart(item)
    },
  },
}
</script>

<style lang="scss">
.pricing-list-item {
  $this: &;
  border: 1px solid var(--tundora-gray);
  border-radius: 10px;
  padding: 15px;
  &__wrapper {
    display: grid;
    grid-template-columns: 40px auto 75px;
    align-items: flex-start;
    width: 100%;
    height: 30px;
    color: var(--white);
    font-size: rem(16);
    line-height: 23px;
    &--promotional-subscription {
      height: unset;
    }
    &--expert-edge-event {
      align-items: center;
    }
    &--skill-dive {
      height: unset;
    }
  }
  &__btn {
    border: none;
    margin: 0;
    cursor: pointer;
    align-self: baseline;
    /* file name: '@/assets/icons/garbage_icon.svg' */
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='22' viewBox='0 0 20 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8 2C7.44772 2 7 2.44772 7 3V4H13V3C13 2.44772 12.5523 2 12 2H8ZM15 4V3C15 1.34315 13.6569 0 12 0H8C6.34315 0 5 1.34315 5 3V4H1C0.447715 4 0 4.44772 0 5C0 5.55228 0.447715 6 1 6H2V19C2 20.6569 3.34315 22 5 22H15C16.6569 22 18 20.6569 18 19V6H19C19.5523 6 20 5.55228 20 5C20 4.44772 19.5523 4 19 4H15ZM4 6V19C4 19.5523 4.44772 20 5 20H15C15.5523 20 16 19.5523 16 19V6H4ZM8 9C8.55228 9 9 9.44771 9 10V16C9 16.5523 8.55228 17 8 17C7.44772 17 7 16.5523 7 16V10C7 9.44771 7.44772 9 8 9ZM12 9C12.5523 9 13 9.44771 13 10V16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16V10C11 9.44771 11.4477 9 12 9Z' fill='white'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px;
  }
  &__selected-name {
    margin: 0 auto 0 10px;
    text-align: left;
    width: 100%;
    @include mobile {
      margin: 0 auto 0 10px;
    }
    &--skill-dive {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      flex-flow: column;
      align-items: flex-start;
      @include desktop {
        flex-flow: row;
        align-items: center;
      }
    }
    #{$this} {
      &__skill-dive {
        &-icon {
          width: 100px;
          height: 26px;
          @include mobile {
            width: 130px;
          }
        }
      }
    }
    &-pill {
      margin: 10px 0;
      font-size: rem(10px);
      line-height: 12px;
      @include desktop {
        font-size: rem(12px);
        line-height: 14px;
      }
      &--expert-edge-event {
        margin: 0;
      }
      &-text {
        &--hide {
          &-mobile {
            display: none;
            @include desktop {
              display: block;
            }
          }
          &-desktop {
            display: block;
            @include desktop {
              display: none;
            }
          }
        }
      }
    }
  }
  &__selected-price {
    font-size: rem(18);
    line-height: 24px;
    text-align: right;
    margin: 0;
  }
  &__promo-warning {
    margin: 5px 0 0 50px;
    color: var(--dusty-gray);
    font-size: rem(12);
    line-height: 14px;
    text-align: left;
    display: grid;
    gap: 5px;
    @include tablet {
      display: block;
      gap: unset;
      margin: 0 0 0 50px;
    }
    p {
      margin-top: 0;
      margin-bottom: 10px;
    }
  }
  &__voucher-wrapper {
    @include tablet {
      padding: 0 20px;
    }
  }
  &__voucher {
    max-height: 42px;
    background: rgba(64, 252, 196, 0.12);
    border: 1px solid #00da99;
    border-radius: 10px;
    padding: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    line-height: 22px;
    @include tablet {
      max-height: 62px;
      padding: 20px;
      font-size: 18px;
    }
    &--price {
      text-transform: uppercase;
      margin: 0 0 0 25px;
    }
  }
  &__verbage {
    color: var(--dusty-gray);
    font-size: rem(12);
    line-height: 14px;
    text-align: left;
    margin: 0 0 15px 50px;
    display: flex;
    &-border {
      height: auto;
      width: 3px;
      border-left: 3px solid var(--white);
      border-radius: 10px;
    }
    &-text {
      margin: 0 0 0 10px;
    }
  }
}
</style>
